// src/store/slices/authSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router-dom'; // To handle navigation after successful login
import { RootState } from 'store';
import { API_BASE } from 'apiConfig';

interface AuthState {
  token: string | null;
  user: object | null;
  license_uuid: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  isMembershipValid: boolean | null; // access gate
  hasMembership: boolean | null; // real paid-membership status (for display/CTA)
  widget_license: string | null; // account public_token to embed in the widget
  is_sub_account: string;
  acceptChats: boolean;
}

const initialState: AuthState = {
  token: localStorage.getItem('authToken')
    ? localStorage.getItem('authToken')!
    : null,
  user: null,
  license_uuid: localStorage.getItem('license_uuid')
    ? localStorage.getItem('license_uuid')!
    : null,
    is_sub_account: "0",
  status: 'idle',
  error: null,
  // null = not yet known; ProtectedRoute shows a checking state rather than
  // briefly rendering the app to someone who turns out to have no membership.
  isMembershipValid: null,
  hasMembership: null,
  widget_license: localStorage.getItem('widget_license') || null,
  acceptChats: true
};

type LoginResponse = {
  token: string;
  token_type: 'Bearer';
  expires_in: number;
  license_uuid: string;
  user: {
    id: number;
    uuid: string;
    email: string;
    name: string;
    membership_valid: string | null;
    parent_uuid: string | null;
    is_sub_account: string | null;
    avatar_url: string | null
  };
  error?: string;
  message?: string;
};



export const login = createAsyncThunk<
  LoginResponse,                                 // return type on success
  { username: string; password: string },        // arg type
  { rejectValue: string }                        // reject payload type
>(
  'auth/login',
  async ({ username, password }, thunkAPI) => {
    try {
      // optional: if you manage status in slice's extraReducers, you can remove this
      thunkAPI.dispatch(setStatus('loading'));

      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: String(username).trim().toLowerCase(),
          password: String(password),
        }),
      });

      const data = (await res.json()) as Partial<LoginResponse>;

      if (!res.ok) {
        const msg =
          (data?.error as string) ||
          (data?.message as string) ||
          `Login failed (HTTP ${res.status})`;
        return thunkAPI.rejectWithValue(msg);
      }

      if (!data?.token) {
        return thunkAPI.rejectWithValue('Malformed response from server.');
      }

      // Persist token (+ user, if you like)
      localStorage.setItem('authToken', data.token);
      if (data.user) {
        
        localStorage.setItem('avatar_url', (data.user.avatar_url || ""));
        localStorage.setItem('name', (data.user.name || ""));
        localStorage.setItem('authUser', JSON.stringify(data.user));
        localStorage.setItem('license_uuid', data.user.parent_uuid || "");

      }

      data.license_uuid = data.user?.parent_uuid ?? undefined;


      // Optionally: thunkAPI.dispatch(setStatus('succeeded'));
      return data as LoginResponse;
    } catch (err: any) {
      const msg = err?.message || 'Unexpected error occurred during login';
      return thunkAPI.rejectWithValue(msg);
    }
  }
);

export const checkMembership = createAsyncThunk<
  {
    isValid: boolean;
    hasMembership: boolean;
    widgetLicense?: string;
    avatar_url?: string;
    name?: string;
    email?: string;
  },
  void,
  { state: RootState; rejectValue: string }
>('auth/checkMembership', async (_, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;

    const response = await fetch(
      `${API_BASE}/check-membership-new/`,
      {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return thunkAPI.rejectWithValue(data?.message || 'Membership check failed');
    }

    return {
      isValid: Boolean(data.isValid),
      hasMembership: Boolean(data.membership_valid),
      widgetLicense: data.public_token ?? undefined,
      avatar_url: data.avatar_url ?? null,   // assuming backend returns avatar_url
      name: data.name ?? "Unnamed",
      email: data.email ?? ""
    };
  } catch (error) {
    return thunkAPI.rejectWithValue('Failed to check membership');
  }
});


type RegisterResponse = {
  success?: boolean;
  requires_confirmation?: boolean;
  email?: string;
  message?: string;
  error?: string;
};

// Self-serve signup. Does NOT log the user in — the account must be confirmed
// via the email link first.
export const register = createAsyncThunk<
  RegisterResponse,
  { name: string; email: string; password: string },
  { rejectValue: string }
>('auth/register', async ({ name, email, password }, thunkAPI) => {
  try {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: String(name).trim(),
        email: String(email).trim().toLowerCase(),
        password: String(password)
      })
    });
    const data = (await res.json()) as RegisterResponse;
    if (!res.ok) {
      return thunkAPI.rejectWithValue(
        data?.error || data?.message || `Registration failed (HTTP ${res.status})`
      );
    }
    return data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err?.message || 'Unexpected error during registration');
  }
});

// Re-send the confirmation email. Always resolves (server is enumeration-safe).
export const resendConfirmation = createAsyncThunk<
  { message?: string },
  { email: string },
  { rejectValue: string }
>('auth/resendConfirmation', async ({ email }, thunkAPI) => {
  try {
    const res = await fetch(`${API_BASE}/auth/resend-confirmation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: String(email).trim().toLowerCase() })
    });
    const data = await res.json();
    if (!res.ok) return thunkAPI.rejectWithValue(data?.error || 'Could not resend confirmation.');
    return data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err?.message || 'Could not resend confirmation.');
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: state => {
      console.log('Logging out'); // Debug log
      state.token = null;
      state.user = null;
      state.status = 'idle';
      state.error = null;
      localStorage.removeItem('authToken');
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setAcceptChats: (state, action) => {
      state.acceptChats = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(login.pending, state => {
        console.log('Login pending'); // Debug log
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        console.log('Login successful', action.payload); // Debug log

        state.token = action.payload.token;
        state.license_uuid = action.payload.license_uuid;
        state.user = action.payload.user;
        state.status = 'succeeded';
        state.error = null;
       localStorage.setItem('authToken', action.payload.token); // ✅
      localStorage.setItem('authUser', JSON.stringify(action.payload.user)); // optional
      })
      .addCase(login.rejected, (state, action) => {
        console.log('Login failed', action.payload); // Debug log
        state.status = 'failed';
        state.error = action.payload as string;
      })
     .addCase(checkMembership.fulfilled, (state, action) => {
        // Gate on the real membership (`membership_valid` from the API), NOT on
        // `isValid` — that field only says the session/user is valid and the API
        // hard-codes it to true, so gating on it never blocked anyone.
        state.isMembershipValid = action.payload.hasMembership;
        state.hasMembership = action.payload.hasMembership;
        if (action.payload.widgetLicense) {
          state.widget_license = action.payload.widgetLicense;
          localStorage.setItem('widget_license', action.payload.widgetLicense);
        }


        if (action.payload.avatar_url) {
          // Update user avatar in state
 

            state.user = {
              ...state.user,
              avatar_url: action.payload.avatar_url,
              name: action.payload.name,
              email: action.payload.email
            };
  
          // Persist in localStorage too
          localStorage.setItem('name', action.payload.name ?? "");
          localStorage.setItem('avatar_url', action.payload.avatar_url);
          localStorage.setItem('authUser', JSON.stringify(state.user));
        }
      })
      .addCase(checkMembership.rejected, (state, action) => {
        // A failed membership check (network blip, CORS, transient 5xx) must NOT
        // log the user out — that caused login to bounce straight back to login.
        // Keep the session and let them through: the API enforces membership on
        // every protected endpoint (402 membership_required), so a client-side
        // fail-open here costs nothing but avoids locking out paying users.
        state.isMembershipValid = true;
        console.warn('Membership check failed (keeping session):', action.payload);
      });
  }
});

export const { logout, setStatus, setAcceptChats } = authSlice.actions;
export default authSlice.reducer;
